import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { IoIosArrowForward } from 'react-icons/io';
import { LuBellOff } from 'react-icons/lu';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import Box from 'components/ui/box';
import Button from 'components/ui/button';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import CustomSelect from 'components/custom-select';
import { NotificationDetailModal } from 'components/notification-detail';
import {
  getNotificationDetail,
  getNotificationInbox,
  markAsRead,
} from 'services/notificationInbox.service';
import { CommunicationSubject } from 'pages/Preference/communication-preferences';
import {
  FAMILY_FILTERS,
  FamilyFilter,
  NotificationDetail,
  NotificationRow,
  rowTitle,
  toReadPayload,
} from './notification-inbox';

const POR_PAGINA = 20;

/**
 * Central de notificações (CENTRAL-04, CENTRAL-05). Lista compacta com assunto,
 * data e estado; abrir renderiza a peça e **mantém o aviso na lista**, em estado
 * lido (CENTRAL-01.2) — o histórico é o motivo de a central existir.
 *
 * Paginação por `skip`/`limit`, não pelo `SortPagination` do projeto: aquele
 * utilitário lê o header `Content-Range`, que um prepared aggregate não emite.
 * Sem total, a navegação é anterior/próxima — e há próxima quando a página veio
 * cheia. Para uma caixa que raramente passa de algumas dezenas de avisos em 90
 * dias, pedir um `$count` extra a cada carregamento pagaria mais do que entrega.
 */
export function Notificacoes() {
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState<FamilyFilter>('todas');
  const [page, setPage] = useState(0);

  const [detail, setDetail] = useState<NotificationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openError, setOpenError] = useState(false);

  const filtroId = useId();

  const load = useCallback(async () => {
    setLoading(true);

    const query: { family?: CommunicationSubject; skip: number; limit: number } = {
      skip: page * POR_PAGINA,
      limit: POR_PAGINA,
    };
    if (family !== 'todas') query.family = family;

    setRows(await getNotificationInbox(query));
    setLoading(false);
  }, [family, page]);

  useEffect(() => {
    load();
  }, [load]);

  async function abrir(row: NotificationRow) {
    setDetailLoading(true);
    setOpenError(false);

    const aberto = await getNotificationDetail(row._id);
    setDetail(aberto);
    setDetailLoading(false);

    // Sem isto o clique não produz efeito visível nenhum: o modal fecha e a
    // pessoa fica achando que a lista travou.
    if (!aberto) {
      setOpenError(true);
      return;
    }
    if (aberto.status === 'read') return;

    await markAsRead(toReadPayload(aberto, Date.now()));

    // O aviso CONTINUA na lista, só muda de estado (CENTRAL-01.2).
    setRows(atual => atual.map(item => {
      if (item._id !== row._id) return item;

      return { ...item, status: 'read' };
    }));
  }

  function trocarFiltro(valor: FamilyFilter) {
    setFamily(valor);
    setPage(0);
  }

  const temProxima = rows.length === POR_PAGINA;
  const filtroAtual = FAMILY_FILTERS.find(option => option.value === family);

  return (
    <main className="mx-auto container p-4 lg:p-10" aria-busy={loading}>
      <nav className="flex flex-row items-center space-x-4 mb-8 lg:mb-12" aria-label="Navegação da página">
        <Link
          to="../"
          className="rounded-full bg-purple flex justify-center items-center p-1 no-underline hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow"
          aria-label="Voltar para página anterior"
        >
          <RiArrowLeftSLine size={28} className="text-white" aria-hidden="true" />
        </Link>
        <h1 className="text-xl lg:text-3xl text-white font-semibold" tabIndex={0}>
          Minhas Notificações
        </h1>
      </nav>

      <Box>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6 lg:mb-8 pb-6 border-b border-white border-opacity-10">
          <div>
            <h2 className="text-lg lg:text-xl text-white font-semibold leading-none mb-2" tabIndex={0}>
              Seus avisos
            </h2>
            <p className="text-white text-opacity-60 text-sm" tabIndex={0}>
              Histórico dos últimos 90 dias.
            </p>
          </div>

          <div className="w-full lg:w-[264px]">
            <label htmlFor={filtroId} className="block mb-2 text-sm">
              Filtrar por assunto
            </label>
            <CustomSelect
              inputId={filtroId}
              ariaLabel="Filtrar por assunto"
              placeholder="Clique para selecionar o assunto"
              options={FAMILY_FILTERS}
              value={filtroAtual}
              onChange={(option: any) => {
                if (!option) return;

                trocarFiltro(option.value as FamilyFilter);
              }}
            />
          </div>
        </div>

        {loading && (
          <div role="status" aria-live="polite" aria-busy="true" className="grid grid-cols-1 gap-3">
            <span className="sr-only">Carregando notificações</span>
            <ArticlePlaceholder />
          </div>
        )}

        {openError && (
          <p role="alert" className="text-center text-sm text-yellow mb-4" tabIndex={0}>
            Não foi possível abrir esta notificação. Tente novamente.
          </p>
        )}

        {/*
          Caixa vazia é o estado mais provável de quem entra aqui pela primeira vez —
          e um card grande com uma frase solta no meio lê como falha de carregamento.
          O ícone ancora, o texto explica quando algo vai aparecer, e o botão só
          existe quando há filtro: sem ele, quem filtrou e não achou nada tem de
          adivinhar que o vazio é do filtro, não da caixa.
        */}
        {!loading && rows.length === 0 && (
          <div role="status" className="flex flex-col items-center text-center py-10 lg:py-14">
            <span className="rounded-full bg-white bg-opacity-5 flex justify-center items-center w-16 h-16 mb-5">
              <LuBellOff size={28} className="text-electric-blue" aria-hidden="true" />
            </span>
            <p className="text-white text-lg mb-2" tabIndex={0}>
              Nenhuma notificação por aqui
            </p>
            <p className="text-white text-opacity-60 font-thin max-w-md" tabIndex={0}>
              {family === 'todas'
                ? 'Quando o programa enviar um aviso para você, ele fica nesta lista pelos 90 dias seguintes.'
                : `Você não recebeu nada em "${filtroAtual?.label}" nos últimos 90 dias.`}
            </p>
            {family !== 'todas' && (
              <Button
                className="uppercase text-sm py-1 px-5 rounded leading-none mt-6"
                onClick={() => trocarFiltro('todas')}
              >
                Ver todos os assuntos
              </Button>
            )}
          </div>
        )}

        {!loading && rows.length > 0 && (
          <ul className="divide-y divide-white divide-opacity-10">
            {rows.map(row => (
              <li key={row._id}>
                <button
                  type="button"
                  className="w-full text-left py-4 px-3 rounded-lg flex items-center justify-between gap-4 hover:bg-white hover:bg-opacity-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow transition-colors"
                  onClick={() => abrir(row)}
                >
                  <span className="flex-1">
                    {/* Não lida em peso e branco pleno; lida recua para 70% — é o que
                        diferencia as duas na varredura, já que a etiqueta some. */}
                    <span
                      className={`block ${
                        row.status === 'unread' ? 'text-white font-semibold' : 'text-white text-opacity-70 font-thin'
                      }`}
                    >
                      {rowTitle(row)}
                    </span>
                    <span className="block text-sm font-thin mt-1 text-white text-opacity-60">
                      <time dateTime={new Date(row.created).toISOString()}>
                        {format(new Date(row.created), 'dd/MM/yyyy', { locale: ptBR })}
                      </time>
                    </span>
                  </span>
                  {row.status === 'unread' && (
                    <span className="shrink-0 rounded-full bg-yellow text-blue-dark text-xs font-semibold px-3 py-1">
                      não lida
                    </span>
                  )}
                  {row.status === 'read' && <span className="sr-only">lida</span>}
                  <IoIosArrowForward size={20} className="shrink-0 text-electric-blue" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {!loading && (page > 0 || temProxima) && (
          <div className="flex justify-center gap-4 mt-8">
            <Button
              className="uppercase text-sm py-1 px-5 rounded leading-none disabled:opacity-40 disabled:hover:ring-0"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Anterior
            </Button>
            <Button
              className="uppercase text-sm py-1 px-5 rounded leading-none disabled:opacity-40 disabled:hover:ring-0"
              disabled={!temProxima}
              onClick={() => setPage(page + 1)}
            >
              Próxima
            </Button>
          </div>
        )}
      </Box>

      <NotificationDetailModal
        detail={detail}
        loading={detailLoading}
        onClose={() => setDetail(null)}
      />
    </main>
  );
}
